/* ============================================================
 * köbi Label-Editor — 3D-Modul (Three.js Bottle-Preview)
 * ============================================================
 * Portiert aus etiketten-tool.html Z.2308–2644.
 *
 * Architektur:
 * - ES-Modul, lädt Three.js + Addons über die Importmap der Section.
 * - Desktop (≥900px): Eager-Init beim Script-Load (unverändertes
 *   Verhalten).
 * - Mobile (≤899.98px): Lazy-Init beim ersten Klick auf den 3D-
 *   Vorschau-Button. window.bottleInit() ist der Trigger; idempotent,
 *   d. h. zweiter Aufruf gibt dasselbe Promise zurück. Three.js + GLB
 *   werden so nur geladen wenn 3D auf Mobile wirklich angefragt wird.
 * - Bridge zum klassischen JS via window.bottle*-Funktionen.
 *
 * Window-API (für koebi-label-editor.js):
 *   window.bottleInit()                  — Lazy-Init (Promise),
 *                                          idempotent (Mobile-Pfad)
 *   window.bottleRefreshView()           — Re-Render + Resize
 *   window.bottleSetCapColor(btn,hex,…)  — Deckel-Farbe + aktiver Swatch
 *   window.bottleApplyLabel()            — liest #previewCanvas via
 *                                          window.renderCleanLabelCanvas()
 *                                          und legt Textur auf Label-Mesh
 *   window.bottleSaveImage()             — 1920²-PNG-Snapshot
 *   window.bottleSaveVideo()             — MP4-360°-Sweep
 *
 * Wartungshinweis: Der Label-Konfigurator in
 * assets/koebi-label-configurator.js spiegelt den Look dieser 3D-Bühne
 * (Licht, Schatten, GTAO/SMAA, Label-Maße). Änderungen daran dort ebenfalls
 * nachvollziehen.
 * ============================================================ */

(async () => {
  'use strict';

  // ── Lazy-Init-Wrapper ────────────────────────────────────────────────────
  // window.bottleInit() startet den initBottle3D()-Pfad genau einmal und
  // gibt dasselbe Promise zurück. Auf Desktop wird er direkt unten
  // aufgerufen (Eager). Auf Mobile triggert enter3DMode() den Init
  // erst beim ersten Klick auf den 3D-Vorschau-Button.
  let initPromise = null;
  window.bottleInit = () => {
    if (initPromise) return initPromise;
    initPromise = initBottle3D();
    return initPromise;
  };

  // Desktop: Eager-Init wie bisher. Mobile: warten auf User-Trigger.
  if (!window.matchMedia('(max-width: 899.98px)').matches) {
    window.bottleInit();
  }

  async function initBottle3D() {
  // ── Wurzel-Element + Canvas ──────────────────────────────────────────────
  const root = document.querySelector('[data-koebi-label-editor]');
  if (!root) return;

  const canvas = document.querySelector('[data-koebi-label-3d-canvas]');
  const wrap = document.getElementById('bottleWrap');
  if (!wrap || !canvas) return;

  const modelUrl = root.dataset.modelUrl;
  if (!modelUrl) {
    console.error('[köbi-label-editor-3d] data-model-url fehlt am [data-koebi-label-editor].');
    return;
  }

  // ── Export-Hintergrund-Konstanten ────────────────────────────────────────
  // Im Viewer kommt der Hintergrund (Card-Background) + Blob aus dem CSS
  // (`.koebi-label-3d-stage__viewer` + `.koebi-label-3d-stage__blob` SVG).
  // Für die Bild- und Video-Exports muss beides aber im Render-Output landen,
  // weil MediaRecorder/toDataURL nur den WebGL-Canvas kennen. Wir komponieren
  // deshalb beim Export auf ein zusätzliches 2D-Canvas:
  //   1. warm-100-Fläche (#EEEBE8) als Background-Fill
  //   2. Blob als Path2D, zentriert, 64% der Breite, gefüllt mit warm-50
  //   3. WebGL-Canvas-Inhalt obendrauf (Renderer rendert weiterhin alpha:0)
  // Pfad-Daten 1:1 aus dem Liquid-Markup übernommen, ViewBox 403×397.
  const EXPORT_BG_COLOR    = '#EEEBE8';   // --koebi-bg-warm-100
  const EXPORT_BLOB_COLOR  = '#F5F3F0';   // --koebi-bg-card / warm-50
  const EXPORT_BLOB_VB_W   = 403;
  const EXPORT_BLOB_VB_H   = 397;
  const EXPORT_BLOB_WIDTH_RATIO = 0.64;   // entspricht der CSS-Regel (`width: 64%`)
  const EXPORT_BLOB_PATH_D = 'M226.96 1.14C275.93 5.69 257.91 87.07 285.81 115.42C317.99 148.13 388.54 143.86 400.96 188.02C413.03 230.95 349.03 258.45 323.03 294.67C296.29 331.92 286.47 393.63 240.78 396.6C195.47 399.55 177.7 340.94 139.24 316.75C101.14 292.78 39.25 293.62 20.41 252.73C1.74 212.21 58.5 176.05 68.96 132.67C78.74 92.14 56.55 34.61 94.66 17.72C134.28 0.16 183.8 -2.87 226.96 1.14Z';
  const EXPORT_BLOB_PATH2D = (typeof Path2D !== 'undefined') ? new Path2D(EXPORT_BLOB_PATH_D) : null;

  // Zeichnet warm-100-Fläche + Blob auf das übergebene 2D-Context. Wird sowohl
  // von bottleSaveImage (einmalig) als auch von bottleSaveVideo (per Frame im
  // recordLoop) verwendet.
  const paintExportBackground = (ctx2d, w, h) => {
    ctx2d.fillStyle = EXPORT_BG_COLOR;
    ctx2d.fillRect(0, 0, w, h);
    if (!EXPORT_BLOB_PATH2D) return;
    const blobW = w * EXPORT_BLOB_WIDTH_RATIO;
    const scale = blobW / EXPORT_BLOB_VB_W;
    const blobH = EXPORT_BLOB_VB_H * scale;
    const offsetX = (w - blobW) / 2;
    const offsetY = (h - blobH) / 2;
    ctx2d.save();
    ctx2d.translate(offsetX, offsetY);
    ctx2d.scale(scale, scale);
    ctx2d.fillStyle = EXPORT_BLOB_COLOR;
    ctx2d.fill(EXPORT_BLOB_PATH2D);
    ctx2d.restore();
  };

  // ── Dynamische Imports (Importmap der Section) ───────────────────────────
  let THREE, GLTFLoader, MeshoptDecoder, RoomEnvironment;
  let EffectComposer, RenderPass, GTAOPass, SMAAPass, OutputPass;
  try {
    THREE           = await import('three');
    ({ GLTFLoader }      = await import('three/addons/loaders/GLTFLoader.js'));
    ({ MeshoptDecoder }  = await import('three/addons/libs/meshopt_decoder.module.js'));
    ({ RoomEnvironment } = await import('three/addons/environments/RoomEnvironment.js'));
    // Post-Processing für AO + Tone-Mapping. EffectComposer ist Three.js'
    // klassische Render-Pipeline; GTAOPass produziert die feinen Self-
    // Shadow-Schattierungen in Cavities/Wölbungen; SMAA glättet Geometrie-
    // Kanten morphologisch nach allen anderen Passes (fängt auch Kanten ab
    // die MSAA verpasst); OutputPass am Ende macht das ACES-Tone-Mapping.
    ({ EffectComposer } = await import('three/addons/postprocessing/EffectComposer.js'));
    ({ RenderPass }     = await import('three/addons/postprocessing/RenderPass.js'));
    ({ GTAOPass }       = await import('three/addons/postprocessing/GTAOPass.js'));
    ({ SMAAPass }       = await import('three/addons/postprocessing/SMAAPass.js'));
    ({ OutputPass }     = await import('three/addons/postprocessing/OutputPass.js'));
  } catch (e) {
    console.error('[3D] Import:', e);
    return;
  }

  // ── Mobile-Detection ─────────────────────────────────────────────────────
  // Auf Mobile (<900 px) wird das 3D-Modul seit 2026-05-13 lazy initialisiert
  // (vorher Frühreturn). iPhone/Android-GPUs schaffen aber nicht die volle
  // Desktop-Pipeline (DPR 4 × MSAA 8 × GTAO 8) bei flüssigen 60fps. Wir
  // reduzieren auf Mobile gezielt:
  //   - DPR cap 1.5 statt 4   (in setSize unten)
  //   - MSAA samples 2 statt 8 (im composerRT unten)
  //   - GTAO samples 4 statt 8 (im gtaoPass unten)
  // Visuell minimaler Qualitätsverlust, FPS-mäßig massiv besser.
  const IS_MOBILE = window.matchMedia('(max-width: 899.98px)').matches;

  // ── Renderer ─────────────────────────────────────────────────────────────
  // alpha:true — der Blob im Hintergrund wird per CSS-SVG hinter den Canvas
  // gelegt (siehe .koebi-label-3d-stage__blob), der Canvas selbst muss also
  // durchsichtig rendern. preserveDrawingBuffer:true bleibt — das Snapshot-/
  // Video-Capture (toDataURL, captureStream) braucht den Backbuffer.
  // premultipliedAlpha:false — WebGL-Standard ist `true`, dann hat das
  // Renderer-Output RGB schon mit Alpha multipliziert. Beim drawImage auf
  // das 2D-Composite-Canvas (das straight-Alpha-Blending macht) gibt es
  // dann an Geometrie-Kanten eine doppelte Alpha-Multiplikation → die
  // semi-transparenten Edge-Pixel werden zu dunkel, was als Stair-Stepping
  // sichtbar wird. Mit straight-Alpha im WebGL-Output matched das blending
  // sauber, Edges bleiben smooth.
  let renderer;
  try {
    renderer = new THREE.WebGLRenderer({
      canvas,
      antialias:             true,
      alpha:                 true,
      premultipliedAlpha:    false,
      preserveDrawingBuffer: true,
    });
  } catch (e) {
    console.error('[3D] WebGL:', e);
    return;
  }
  renderer.setClearColor(0x000000, 0);
  renderer.outputColorSpace    = THREE.SRGBColorSpace;
  // ACESFilmic bleibt am Renderer gesetzt. Im Composer-Pfad rendert der
  // Renderer in Render-Targets (linear), WENDET dabei aber NICHT auto-
  // matisch das Tone-Mapping an. Stattdessen liest der OutputPass am Ende
  // der Composer-Chain `renderer.toneMapping` aus und appliziert ihn dort
  // einmal. NoToneMapping hier hätte den OutputPass auf Passthrough
  // geschaltet → flacher, blasser Look (das war der „blass"-Bug aus
  // Session 11).
  renderer.toneMapping         = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 0.9;
  // Self-Shadows aktivieren. PCFSoft = weiche Schatten-Kanten via
  // Percentage-Closer-Filtering, wirkt natürlicher als hard-edged Shadows.
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type    = THREE.PCFSoftShadowMap;

  // ── Kamera ───────────────────────────────────────────────────────────────
  const camera = new THREE.PerspectiveCamera(17, 1, 0.01, 100);
  camera.position.set(0, -0.1, 6.1);
  camera.lookAt(0, 0, 0);

  // Composer wird erst nach Scene+Lights gebaut — vorher deklariert, damit
  // setSize() ihn referenzieren kann ohne TDZ-Error beim ersten Aufruf.
  let composer = null;

  const setSize = () => {
    const s = Math.round(wrap.getBoundingClientRect().width);
    // Guard gegen 0×0: der wrap sitzt initial in einer `display:none`-Stage
    // (3D-Mode noch aus), `getBoundingClientRect()` gibt dann 0 zurück. Wenn
    // wir Renderer/Composer mit Größe 0 initialisieren, baut EffectComposer
    // intern RenderTargets mit Größe 0 → WebGL meldet sich mit
    // „Framebuffer is incomplete: Attachment has zero size". Solange der wrap
    // unsichtbar ist, lassen wir die alten Render-Target-Dimensionen stehen.
    // bottleRefreshView() ruft setSize nach dem Mode-Wechsel nochmal auf,
    // dann mit echten Pixeln.
    if (s <= 0) return;
    // PixelRatio: auf Mobile auf 1.5 cappen (iPhone GPU schafft die volle
    // Pipeline bei DPR×2 nicht flüssig). Auf Desktop bleibt das Supersampling
    // mit DPR×2 (cap 4) — Schärfe für High-DPI-Monitore.
    const dpr = IS_MOBILE
      ? Math.min(window.devicePixelRatio || 1, 1.5)
      : Math.min(window.devicePixelRatio * 2, 4);
    renderer.setPixelRatio(dpr);
    renderer.setSize(s, s, false);
    canvas.style.width = ''; canvas.style.height = '';
    camera.aspect = 1; camera.updateProjectionMatrix();
    // Composer muss synchron resized werden — UND seinen PixelRatio mit dem
    // Renderer syncen. Der Composer cached den DPR-Wert intern beim Konstruk-
    // tor; wenn er beim Construktor mit DPR=1 erstellt wurde (weil der Initial-
    // setSize-Call wegen display:none geskipped war), würde er beim setSize
    // weiterhin mit DPR=1 rechnen → das Render-Target hat die halbe Auflösung
    // vom Canvas, GTAOPass arbeitet auf der halben Auflösung, finaler Output
    // wird hochskaliert → pixelig.
    if (composer) {
      composer.setPixelRatio(dpr);
      composer.setSize(s, s);
    }
  };
  setSize();
  window.addEventListener('resize', () => { setSize(); requestRender(); });

  // ── Scene + Beleuchtung ──────────────────────────────────────────────────
  const scene = new THREE.Scene();
  try {
    const pmrem = new THREE.PMREMGenerator(renderer);
    scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    scene.environmentIntensity = 0.35;
    pmrem.dispose();
  } catch (_) {}

  // Tageslicht ~6500 K
  const DAY = 0xeef4ff;

  scene.add(new THREE.AmbientLight(DAY, 0.14));

  // KEY-Light — hoch genug für klaren Schlagschatten, aber etwas zur Seite
  // gerückt (X 3.0 → 5.0). Bei sehr steiler Beleuchtung von direkt oben
  // projiziert der Cap-Rand auf den Cap selbst statt auf den Hals. Ein
  // 30–40°-Winkel von vertikal gibt einen seitlich verschobenen Schlag-
  // schatten der auf dem Hals landet und damit sichtbar wird.
  const key = new THREE.DirectionalLight(DAY, 4.64);
  key.position.set(5.0, 8.0, 2.5);
  key.castShadow = true;
  // Shadow-Map 1024² (statt 2048²) — für 1080-px-Output ist 1024 mehr als
  // ausreichend, und re-renderen der Shadow-Map pro Frame ist auf 1024² 4x
  // schneller als auf 2048².
  key.shadow.mapSize.set(1024, 1024);
  key.shadow.camera.left   = -1.5;
  key.shadow.camera.right  =  1.5;
  key.shadow.camera.top    =  1.5;
  key.shadow.camera.bottom = -1.5;
  key.shadow.camera.near   =  0.1;
  key.shadow.camera.far    = 18;
  // Bias drastisch reduziert. Das vorherige normalBias=0.02 hat den Schatten
  // ca. 0.02 Welteinheiten (1.3% der Flaschenhöhe) vom Receiver weggeschoben.
  // Wenn der Cap-Boden direkt auf dem Hals sitzt (kein Welt-Spalt), fiel der
  // Schatten damit komplett HINTER den Hals und war unsichtbar. Kleinere
  // Werte hängen den Schatten enger an die Geometrie; Acne wird bei dieser
  // Bottle-Geometrie nicht erwartet, weil die Flächen relativ smooth sind.
  key.shadow.bias    = -0.0001;
  key.shadow.normalBias = 0.004;
  key.shadow.radius  = 4;
  if ('intensity' in key.shadow) key.shadow.intensity = 1.0;
  scene.add(key);

  // FILL — Y 4.0 → 7.0.
  const fill = new THREE.DirectionalLight(DAY, 1.12);
  fill.position.set(-3.5, 7.0, 1.5);
  scene.add(fill);

  // RIM — Y 7.0 → 10.0.
  const rim = new THREE.DirectionalLight(DAY, 2.8);
  rim.position.set(-0.5, 10.0, -3.5);
  scene.add(rim);

  // TOP — Y 9.0 → 14.0.
  const top = new THREE.DirectionalLight(DAY, 1.76);
  top.position.set(0, 14.0, 0.5);
  scene.add(top);

  // ── Post-Processing-Pipeline ─────────────────────────────────────────────
  // Reihenfolge: Scene-Render → Ground-Truth-AO → Tone-Mapping-Output.
  //
  // GTAOPass macht die Cavity-Schattierungen (Deckel-Hals-Übergang, Wölbung
  // der Flasche, jede Konkavität). Liefert das was Tim gemeint hat: das
  // Objekt wirft Mikro-Schatten auf sich selbst.
  //
  // OutputPass am Ende übernimmt das ACES-Tone-Mapping vom Renderer
  // (Renderer ist deshalb auf NoToneMapping gesetzt, sonst Doppel-Tonemap).
  // Composer mit MULTISAMPLED RenderTarget. samples:8 — maximale Hardware-
  // MSAA, moderne GPUs (Apple Silicon, NVIDIA, AMD seit Generation 2017+)
  // unterstützen das. Mit dem SMAA-Pass als zweiter AA-Stufe sollten die
  // Kanten endgültig smooth sein.
  const composerRT = new THREE.WebGLRenderTarget(1, 1, {
    type: THREE.HalfFloatType,
    // Mobile-GPUs schaffen 8×MSAA bei 1400² nicht flüssig — auf 2 reduziert.
    samples: IS_MOBILE ? 2 : 8,
  });
  composer = new EffectComposer(renderer, composerRT);
  composer.addPass(new RenderPass(scene, camera));

  // GTAOPass-Größe wird in setSize() pro Resize aktualisiert. Initialer Wert
  // ist egal — der erste setSize-Call überschreibt ihn vor dem ersten Render.
  const gtaoPass = new GTAOPass(scene, camera, 1, 1);
  // Default-Output: AO modulert über das Scene-Render (Composite-Modus).
  if (GTAOPass.OUTPUT && 'Default' in GTAOPass.OUTPUT) {
    gtaoPass.output = GTAOPass.OUTPUT.Default;
  }
  // Sanfte AO-Stärke — zu aggressiv wirkt schmutzig.
  if (gtaoPass.updateGtaoMaterial) {
    gtaoPass.updateGtaoMaterial({
      radius:           0.25,   // Sample-Radius in World-Space-Einheiten
      distanceExponent: 1.0,
      thickness:        1.0,
      scale:            1.2,    // Verstärkungs-Multiplier
      samples:          IS_MOBILE ? 4 : 8,  // Mobile: 4 reichen, GPU-Budget knapp.
    });
  }
  composer.addPass(gtaoPass);
  // SMAA — Subpixel Morphological Anti-Aliasing. Läuft nach GTAO und greift
  // alle Kanten die MSAA + Supersample-Downsample noch nicht sauber gemacht
  // haben (Geometrie-Silhouetten, Material-Übergänge, Specular-Highlights).
  // Initialdimensionen 1×1 wie bei GTAOPass — composer.setSize() ruft
  // pass.setSize() durch und korrigiert auf reale Pixel.
  const smaaPass = new SMAAPass(1, 1);
  composer.addPass(smaaPass);
  composer.addPass(new OutputPass());

  // Nach Composer-Init nochmal setSize, damit der Composer die korrekten
  // Render-Target-Dimensionen vom Wrap-Element bekommt (initial setSize-Call
  // weiter oben hatte composer noch null gefunden).
  setSize();

  const renderScene = () => composer.render();
  let renderQueued = false;
  let motionFrame = 0;

  const requestRender = () => {
    if (videoRecording || renderQueued) return;
    renderQueued = true;
    requestAnimationFrame(() => {
      renderQueued = false;
      if (!videoRecording) renderScene();
    });
  };

  const startMotionLoop = () => {
    if (videoRecording || motionFrame) return;
    const step = () => {
      motionFrame = 0;
      if (videoRecording) return;
      if (!isDragging) {
        rotY += velY;
        velY *= MOMENTUM_DECAY;
        if (Math.abs(velY) < 0.0001) velY = 0;
      }
      if (modelRoot) modelRoot.rotation.y = rotY;
      renderScene();
      if (isDragging || Math.abs(velY) >= 0.0001) {
        motionFrame = requestAnimationFrame(step);
      } else {
        // Motion ist zu Ende — GTAO wieder einschalten und einmal mit voller
        // Qualität rendern. Während Drag/Momentum bleibt GTAO aus (siehe
        // mousedown-Handler), damit die Framerate hoch bleibt.
        if (gtaoPass && !gtaoPass.enabled) {
          gtaoPass.enabled = true;
          requestRender();
        }
      }
    };
    motionFrame = requestAnimationFrame(step);
  };

  // ── Hintergrund-Layer ────────────────────────────────────────────────────
  // Der Blob hinter der Flasche kommt jetzt als CSS-SVG aus dem Markup
  // (.koebi-label-3d-stage__blob), nicht mehr als 3D-Plane im Renderer.
  // Renderer läuft mit alpha:true (siehe oben), die Section-Card-Background
  // + die SVG-Blob-Form scheinen also direkt durch.

  // ── State ────────────────────────────────────────────────────────────────
  let isDragging = false, lastX = 0, rotY = -Math.PI * 0.4, velY = 0;
  const DRAG_ROTATION_SPEED = IS_MOBILE ? 0.016 : 0.008;
  const MOMENTUM_DECAY = IS_MOBILE ? 0.94 : 0.92;
  let modelRoot = null, labelMesh = null, deckelMesh = null, videoRecording = false;
  // Optional: alter Rotations-Slider (im aktuellen Section-Markup nicht vorhanden)
  const slider = document.getElementById('bottleRotSlider');

  const syncSlider = () => {
    if (!slider) return;
    const deg = ((rotY * 180 / Math.PI) % 360 + 360) % 360;
    slider.value = deg.toFixed(1);
  };
  syncSlider();

  if (slider) {
    slider.addEventListener('input', () => {
      rotY = parseFloat(slider.value) * Math.PI / 180;
      velY = 0;
      if (modelRoot) modelRoot.rotation.y = rotY;
      requestRender();
    });
  }

  // ── Drag — nur horizontal ────────────────────────────────────────────────
  // Pointer-Events statt Mouse-Events: feuern für Maus, Touch UND Stylus
  // einheitlich. Auf Mobile entstanden sonst keine Mouse-Events (Touch wird
  // nicht emuliert) → Flasche ließ sich nicht drehen.
  //
  // Pattern: pointerdown am Canvas, pointermove/up/cancel auf window.
  // Das ist robuster als Pointer-Capture (auf manchen Mobile-Browsern
  // bricht setPointerCapture mit DOMException) und entspricht dem alten
  // mouse-Pattern 1:1 — Drag bleibt aktiv, auch wenn der Finger das
  // Canvas verlässt. Kein `pointerleave`-Handler (würde auf Touch oft
  // unmittelbar nach pointerdown feuern und den Drag killen).
  //
  // `e.preventDefault()` im pointerdown verhindert iOS-typische Side-
  // Effects: Tap-Highlight, Text-Selection-Bubble, double-tap-Zoom.
  // Wirkt zusammen mit `touch-action: none` (im CSS) als doppelte Sperre
  // gegen die Page-Scroll-Interpretation.
  let pointerActive = false;
  canvas.addEventListener('pointerdown', e => {
    if (videoRecording) return;
    // Nur Primärfinger / Primärtaste — schützt vor Multi-Touch-Pinch.
    if (e.button !== undefined && e.button !== 0) return;
    e.preventDefault();
    isDragging = true; pointerActive = true; lastX = e.clientX; velY = 0;
    canvas.style.cursor = 'grabbing';
    // GTAO während Drag/Momentum aus — der AO-Beitrag dreht sich mit der
    // Bottle mit, ist aber während schneller Rotation visuell uninteressant
    // und kostet ~3–5 ms/Frame. Beim Motion-Stop kommt's automatisch wieder
    // (siehe startMotionLoop-step).
    if (gtaoPass) gtaoPass.enabled = false;
    startMotionLoop();
  });
  // pointermove/up auf window — klassisches Drag-Pattern, fängt
  // den Drag auch dann ab, wenn der Finger das Canvas verlässt.
  window.addEventListener('pointermove', e => {
    if (!isDragging || !pointerActive) return;
    velY = (e.clientX - lastX) * DRAG_ROTATION_SPEED; rotY += velY; lastX = e.clientX;
    syncSlider();
    if (modelRoot) modelRoot.rotation.y = rotY;
    startMotionLoop();
  });
  const endDrag = () => {
    if (!isDragging) return;
    isDragging = false; pointerActive = false;
    canvas.style.cursor = 'grab';
  };
  window.addEventListener('pointerup',     endDrag);
  window.addEventListener('pointercancel', endDrag);

  // ── GLB laden ────────────────────────────────────────────────────────────
  // Das Init-Promise (initBottle3D) muss auf das GLB warten, sonst rennt
  // bottleApplyLabel() in `if (!labelMesh) return` weil das Mesh noch nicht
  // existiert. Auf Desktop war das egal (GLB längst geladen wenn User klickt),
  // auf Mobile triggert enter3DMode den Init JETZT und ruft sofort apply.
  // Wir wrappen loader.load in ein Promise und awaiten es am Ende der
  // Init-Funktion (siehe `await glbPromise` weiter unten).
  const loadingEl = document.querySelector('[data-koebi-label-3d-loading]');
  const loader = new GLTFLoader();
  loader.setMeshoptDecoder(MeshoptDecoder);
  const glbPromise = new Promise((resolveGlb) => {
  loader.load(modelUrl,
    (gltf) => {
      const box  = new THREE.Box3().setFromObject(gltf.scene);
      const sz   = box.getSize(new THREE.Vector3());
      gltf.scene.scale.setScalar(1.5 / Math.max(sz.x, sz.y, sz.z));
      const sBox = new THREE.Box3().setFromObject(gltf.scene);
      const ctr  = sBox.getCenter(new THREE.Vector3());
      gltf.scene.position.set(-ctr.x, -ctr.y, -ctr.z);
      const pivot = new THREE.Group();
      pivot.add(gltf.scene);
      gltf.scene.traverse(c => {
        if (c.isMesh) {
          // Self-Shadows aktivieren: jedes Mesh wirft UND empfängt Schatten,
          // damit z. B. der Deckel einen Schatten auf den Flaschenhals
          // werfen kann (und umgekehrt für asymmetrische Lichtwinkel).
          c.castShadow    = true;
          c.receiveShadow = true;
        }
        if (c.isMesh && c.name.toLowerCase() === 'label')  labelMesh  = c;
        if (c.isMesh && c.name.toLowerCase() === 'deckel') deckelMesh = c;
      });
      // Label-Mesh: castShadow ausschalten. Mit `transparent: true` (für
      // PNG-Labels mit Alpha) würde der Three.js-Shadow-Pass eine opake
      // Silhouette des Etiketten-Quads casten — das gibt ein eckiges Schatten-
      // Rechteck auf dem Flaschenkörper hinter dem Etikett, was hässlich
      // aussieht. receiveShadow bleibt an, damit der Cap-Shadow auf dem Etikett
      // sichtbar bleibt wenn er bis dahin reicht.
      if (labelMesh) labelMesh.castShadow = false;
      // Label: MeshStandardMaterial damit Lichter wirken
      if (labelMesh) {
        const oldMap = labelMesh.material?.map ?? null;
        labelMesh.material = new THREE.MeshStandardMaterial({ map: oldMap, transparent: true, roughness: 0.85, metalness: 0.0 });
      }
      // Deckel komplett schwarz (#000000). Im Custom-Etikett-Editor gibt's
      // keine Sortenwahl mehr → der Kronenkorken ist konstant pechschwarz.
      if (deckelMesh) {
        deckelMesh.material = new THREE.MeshStandardMaterial({
          color: new THREE.Color('#000000'),
          roughness: 0.5,
          metalness: 0.65,
        });
      }
      scene.add(pivot); modelRoot = pivot;
      if (loadingEl) loadingEl.hidden = true;
      requestRender();
      resolveGlb();
    },
    null,
    err => {
      console.error('[3D] GLB:', err);
      if (loadingEl) loadingEl.textContent = '3D-Modell konnte nicht geladen werden.';
      // Auch bei Fehler resolven, damit das Init-Promise nicht ewig hängt.
      // bottleApplyLabel() macht dann seinen Frühreturn auf labelMesh=null —
      // die Sidebar/Editor bleiben funktional.
      resolveGlb();
    }
  );
  }); // /glbPromise

  // ── Öffentliche API ──────────────────────────────────────────────────────
  window.bottleRefreshView = () => {
    setSize();
    requestRender();
  };

  window.bottleSetCapColor = (btn, hex, metalness = 0, roughness = 0.195) => {
    document.querySelectorAll('.bottle-cap-swatch').forEach(b => b.classList.remove('active'));
    if (btn) btn.classList.add('active');
    if (!deckelMesh) return;
    if (!(deckelMesh.material instanceof THREE.MeshStandardMaterial)) {
      deckelMesh.material = new THREE.MeshStandardMaterial({});
    }
    deckelMesh.material.color.set(hex);
    deckelMesh.material.metalness = metalness;
    deckelMesh.material.roughness = roughness;
    deckelMesh.material.needsUpdate = true;
    requestRender();
  };

  window.bottleApplyLabel = async () => {
    if (!labelMesh) return;
    if (typeof window.renderCleanLabelCanvas !== 'function') {
      console.warn('[3D] window.renderCleanLabelCanvas() fehlt — Label-Apply abgebrochen.');
      return;
    }
    // Hinweis: vorher checkten wir hier `previewCanvas.style.display === 'none'`,
    // um zu bailen wenn der 2D-Editor noch nichts gerendert hatte. Das ist
    // mit dem 3D-Mode-Toggle hinfällig — die 3D-Vorschau wird explizit per
    // Klick getriggert, der State ist garantiert da, und wir wollen JEDEN
    // Klick als frisches Apply behandeln (sonst zeigt die Flasche nach
    // Zurück + Änderung + erneutem Klick noch den alten Stand).
    const clean = await window.renderCleanLabelCanvas();
    const tex = new THREE.CanvasTexture(clean);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.flipY      = false;
    tex.needsUpdate = true;

    // Material-Map IN-PLACE tauschen statt das ganze Material zu ersetzen.
    // Material-Swap hatte gelegentlich GPU-State-Reuse-Probleme: die Flasche
    // zeigte beim zweiten Apply die Textur vom ersten Apply. Das Update der
    // .map-Property ist Three.js' empfohlener Pfad für dynamische Texturen.
    if (!(labelMesh.material instanceof THREE.MeshStandardMaterial)) {
      labelMesh.material = new THREE.MeshStandardMaterial({
        transparent: true, roughness: 0.85, metalness: 0.0,
      });
    }
    if (labelMesh.material.map && labelMesh.material.map !== tex) {
      labelMesh.material.map.dispose();
    }
    labelMesh.material.map = tex;
    labelMesh.material.transparent = true;
    labelMesh.material.roughness   = 0.85;
    labelMesh.material.metalness   = 0.0;
    labelMesh.material.needsUpdate = true;

    requestRender();
    const btnVid = document.getElementById('btnBottleVideo');
    const btnImg = document.getElementById('btnBottleImage');
    if (btnVid) btnVid.disabled = false;
    if (btnImg) btnImg.disabled = false;
  };

  window.bottleSaveImage = () => {
    if (!modelRoot) return;
    const origPR = renderer.getPixelRatio();
    // Renderer bleibt transparent — wir komponieren später Background+Blob+
    // WebGL auf ein 2D-Composite-Canvas. So entspricht der Export-Look 1:1
    // dem Viewer-Look (warm-100-Fläche + warm-50-Blob + Flasche darüber).
    // Renderer + Composer auf 1:1 1920×1920 ohne Supersample — die MSAA-RTs
    // (samples=4) liefern das Anti-Aliasing, kein Bedarf für zusätzliches
    // Pixel-Oversample.
    renderer.setPixelRatio(1);
    renderer.setSize(1920, 1920, false);
    composer.setPixelRatio(1);
    composer.setSize(1920, 1920);
    camera.aspect = 1; camera.updateProjectionMatrix();
    renderScene();

    // Composite: 1920² 2D-Canvas, Background + Blob → WebGL-Output drauf.
    const composite = document.createElement('canvas');
    composite.width = 1920; composite.height = 1920;
    const cctx = composite.getContext('2d');
    cctx.imageSmoothingEnabled = true;
    cctx.imageSmoothingQuality = 'high';
    paintExportBackground(cctx, 1920, 1920);
    cctx.drawImage(renderer.domElement, 0, 0, 1920, 1920);
    const url = composite.toDataURL('image/png');

    // Renderer zurück auf Viewer-Größe.
    renderer.setPixelRatio(origPR);
    setSize(); camera.aspect = 1; camera.updateProjectionMatrix();
    renderScene();

    const a = document.createElement('a'); a.href = url; a.download = 'flasche-vorschau.png';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  // WebCodecs-Support-Check für sauberen MP4-Export.
  // MediaRecorder produziert in Chrome zwar `video/mp4`, aber als „fragmented
  // MP4" ohne `moov`-Atom am Anfang — von Standard-Playern nicht abspielbar.
  // WebCodecs (VideoEncoder + AVC) + mp4-muxer mit `fastStart: 'in-memory'`
  // baut einen properly formatierten MP4-Container mit moov vorne.
  // Probiert mehrere AVC-Profile in absteigender Kompatibilität; gibt das
  // erste unterstützte zurück oder null.
  // Desktop-Export-Parameter. Mobile überschreibt Größe/FPS/Bitrate im
  // Exportpfad bewusst nach unten, damit WebGL + WebCodecs auf Handy-GPUs
  // zuverlässig fertig werden.
  const MP4_FPS     = 60;             // 60 fps für smoothe Rotation
  // 80 Mbit/s bei 1920×1920@60fps. Pro Frame ~1.33 Mbit. H.264-High-Profile
  // mit B-Frames + VBR; das ist Premium-Quality-Bereich, alle restlichen
  // Kompressions-Artefakte sollten damit weg sein. Datei wird ~40 MB für
  // 4-Sekunden-Clip, immer noch teilbar.
  const MP4_BITRATE = 80_000_000;

  const findSupportedAvcCodec = async (w, h, bitrate, framerate) => {
    if (typeof window.VideoEncoder === 'undefined') {
      console.warn('[3D-MP4] VideoEncoder (WebCodecs) ist im Browser nicht verfügbar.');
      return null;
    }
    // Codec-Strings haben das Format `avc1.<Profile><Level>`:
    //   Profile  64 = High,  4D = Main,  42 = Baseline (+ const_set)
    //   Level    28 = 4.0,   32 = 5.0,   33 = 5.1,    34 = 5.2
    //
    // Level-Caps (was die jeweilige Level erlaubt):
    //   4.0 → max 1080p30 / ~25 Mbit/s
    //   5.0 → max 1080p60 / 135 Mbit/s
    //   5.1 → max 4K30   / 240 Mbit/s
    //   5.2 → max 4K60   / 240 Mbit/s
    //
    // Die Kandidaten sind bewusst breit: Desktop fragt 1920²@60 ab, Mobile
    // 1080²@30. High Profile zuerst, dann Main, dann Baseline; innerhalb
    // jedes Profils zuerst höhere Level, dann kompatiblere Fallbacks.
    const candidates = [
      'avc1.640034',   // High @ Level 5.2 — Premium, 4K60 capable
      'avc1.640033',   // High @ Level 5.1
      'avc1.640032',   // High @ Level 5.0
      'avc1.640028',   // High @ Level 4.0 (Fallback bei kleinen Specs)
      'avc1.4D4034',   // Main @ Level 5.2
      'avc1.4D4033',   // Main @ Level 5.1
      'avc1.4D4032',   // Main @ Level 5.0
      'avc1.4D4028',   // Main @ Level 4.0
      'avc1.42E028',   // Baseline @ Level 4.0
      'avc1.42E01E',   // Baseline @ Level 3.0 (Last-Resort)
    ];
    for (const codec of candidates) {
      try {
        const support = await window.VideoEncoder.isConfigSupported({
          codec, width: w, height: h, bitrate, framerate,
        });
        if (support && support.supported) {
          console.log('[3D-MP4] AVC-Codec verfügbar:', codec);
          return codec;
        }
      } catch (e) { /* probiere nächsten Kandidaten */ }
    }
    console.warn('[3D-MP4] Kein AVC-Codec von VideoEncoder unterstützt.');
    return null;
  };

  window.bottleSaveVideo = async () => {
    if (!modelRoot || videoRecording) return;

    const btnVid = document.getElementById('btnBottleVideo');
    const btnImg = document.getElementById('btnBottleImage');
    if (btnVid) btnVid.disabled = true;
    if (btnImg) btnImg.disabled = true;

    const progWrap  = document.getElementById('bottleProgressWrap');
    const progFill  = document.querySelector('[data-koebi-label-progress-bar]')
                   || document.getElementById('bottleProgressFill');
    const progLabel = document.querySelector('[data-koebi-label-progress-text]')
                   || document.getElementById('bottleProgressLabel');
    // Der Track rendert den Fortschritt als linear-gradient via CSS-
    // Variable `--koebi-progress` (siehe koebi-label-editor.css).
    // Track ist der Parent des Fill-Elements im Liquid-Markup.
    const progTrack = progFill && progFill.parentElement;
    const setProgress = (pct, label) => {
      if (progTrack) {
        progTrack.style.setProperty('--koebi-progress', pct + '%');
      }
      if (progLabel) progLabel.textContent = label;
    };
    if (progWrap) { progWrap.style.display = 'flex'; progWrap.hidden = false; }
    setProgress(0, 'Vorbereiten …');
    // UI-Yield: ohne diesen rAF-Yield wäre die eben gesetzte Progress-UI
    // nicht sichtbar bevor Setup/Encoder loslegen. Doppeltes rAF, damit der
    // Style wirklich committed UND ein Paint-Frame durch ist.
    await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));

    // Auf Mobile kann der User direkt nach Öffnen des Overlays auf "Video"
    // tippen, während der initiale Label-Apply-Retry noch läuft. Vor dem
    // Export deshalb einmal explizit awaiten, damit kein leerer/alter
    // Texturzustand in die Frames geraten kann.
    if (typeof window.bottleApplyLabel === 'function') {
      setProgress(0, 'Bereite Label vor …');
      try { await window.bottleApplyLabel(); }
      catch (e) { console.warn('[3D-MP4] Label-Apply vor Export fehlgeschlagen:', e); }
      await new Promise(r => requestAnimationFrame(r));
    }

    videoRecording = true;
    const startY = rotY;

    // Desktop bleibt Premium-Export. Mobile bekommt eine eigene, realistische
    // Pipeline: 1080², 30fps, 120 Frames, kein 3×Supersampling. Die alte
    // Mobile-Kombination 1920² × DPR 3 × 60fps × 240 Frames hat effektiv
    // 5760²-RenderTargets allokiert und konnte Mobile-GPUs/Encoder sichtbar
    // überfahren (Blinken, Label weg, 99%-Hänger).
    const REC_SIZE = IS_MOBILE ? 1080 : 1920;
    const DURATION = 4;
    const FPS = IS_MOBILE ? 30 : MP4_FPS;
    const BITRATE = IS_MOBILE ? 16_000_000 : MP4_BITRATE;
    const EXPORT_PIXEL_RATIO = IS_MOBILE ? 1 : 3;
    const TOTAL_FRAMES = DURATION * FPS;

    const origPR = renderer.getPixelRatio();
    const gtaoWasEnabled = gtaoPass ? gtaoPass.enabled : false;
    // Mobile-Export rendert direkt über den Renderer statt durch
    // Composer/GTAO/SMAA. Das spart den teuren Postprocessing-RenderTarget-
    // Pfad und vermeidet die Resize-/Framebuffer-Blinker im sichtbaren Canvas.
    if (IS_MOBILE && gtaoPass) gtaoPass.enabled = false;
    const renderVideoScene = IS_MOBILE
      ? () => renderer.render(scene, camera)
      : () => renderScene();

    renderer.setPixelRatio(EXPORT_PIXEL_RATIO);
    renderer.setSize(REC_SIZE, REC_SIZE, false);
    if (!IS_MOBILE) {
      composer.setPixelRatio(EXPORT_PIXEL_RATIO);
      composer.setSize(REC_SIZE, REC_SIZE);
    }
    camera.aspect = 1; camera.updateProjectionMatrix();

    // Sofortiger Render + rAF-Yield: nach dem PixelRatio/Size-Switch hat der
    // Renderer neue Buffer alloziert; ohne diesen erzwungenen ersten Render
    // sieht der User während des restlichen Setup-Blocks ein transparentes
    // Canvas (Label scheint zu verschwinden). Mit Render + rAF malt der
    // Browser den neuen Frame bevor wir weiter blockieren.
    renderVideoScene();
    await new Promise(r => requestAnimationFrame(r));
    setProgress(0, 'Render läuft …');

    // Composite-Canvas: 2D-Background + WebGL-Output drauf. Wird pro Frame
    // neu zusammengesetzt, weil das WebGL-Canvas sich pro Bottle-Rotation ändert.
    const composite = document.createElement('canvas');
    composite.width = REC_SIZE; composite.height = REC_SIZE;
    const cctx = composite.getContext('2d');
    // High-Quality-Smoothing für drawImage. Bei 1:1-Maßen technisch unnötig,
    // schadet aber nicht und gibt einen Tick mehr Sicherheit gegen subpixel-
    // Aliasing.
    cctx.imageSmoothingEnabled = true;
    cctx.imageSmoothingQuality = 'high';

    // Warmup (gegen schwarzen ersten Frame).
    for (let i = 0; i < 3; i++) {
      renderVideoScene();
      paintExportBackground(cctx, REC_SIZE, REC_SIZE);
      cctx.drawImage(renderer.domElement, 0, 0, REC_SIZE, REC_SIZE);
    }

    const cleanup = (blob, ext, shouldDownload = true) => {
      videoRecording = false; rotY = startY;
      if (gtaoPass) gtaoPass.enabled = gtaoWasEnabled;
      if (modelRoot) modelRoot.rotation.y = startY;
      renderer.setPixelRatio(origPR);
      setSize(); camera.aspect = 1; camera.updateProjectionMatrix();
      renderScene();
      if (progWrap) { progWrap.style.display = 'none'; progWrap.hidden = true; }
      if (btnVid) btnVid.disabled = false;
      if (btnImg) btnImg.disabled = false;
      if (!shouldDownload || !blob || !ext) return;
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `flasche-drehung.${ext}`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(a.href), 60_000);
    };

    const renderFrameI = (i) => {
      modelRoot.rotation.y = startY + (i / TOTAL_FRAMES) * Math.PI * 2;
      renderVideoScene();
      paintExportBackground(cctx, REC_SIZE, REC_SIZE);
      cctx.drawImage(renderer.domElement, 0, 0, REC_SIZE, REC_SIZE);
    };

    // MP4 only — kein WebM-Fallback. Wenn WebCodecs nicht verfügbar ist oder
    // der mp4-muxer fehlt, wird eine klare Fehlermeldung gezeigt statt still
    // ein anderes Format zu erzeugen.

    const codec = await findSupportedAvcCodec(REC_SIZE, REC_SIZE, BITRATE, FPS);
    if (!codec) {
      console.error('[3D-MP4] Browser unterstützt keinen H.264-Encoder (VideoEncoder/WebCodecs).');
      alert('Dein Browser unterstützt keinen MP4-Export. Bitte nutze die aktuelle Version von Chrome, Edge, Firefox 130+, oder Safari 17+.');
      cleanup(null, null, false);
      return;
    }

    let muxerModule;
    try {
      // mp4-muxer dynamisch vom CDN laden. esm.sh transformiert automatisch
      // zu ESM-Modulen — robuster als jsDelivr's /+esm Pfad.
      muxerModule = await import('https://esm.sh/mp4-muxer@5.1.5');
      console.log('[3D-MP4] mp4-muxer geladen:', Object.keys(muxerModule).join(', '));
    } catch (e) {
      console.error('[3D-MP4] mp4-muxer-Import fehlgeschlagen:', e);
      alert('MP4-Export-Library konnte nicht geladen werden (Netzwerk-Fehler). Bitte später nochmal probieren.');
      cleanup(null, null, false);
      return;
    }

    const { Muxer, ArrayBufferTarget } = muxerModule;
    if (!Muxer || !ArrayBufferTarget) {
      console.error('[3D-MP4] mp4-muxer-Exports nicht gefunden. Module:', muxerModule);
      alert('MP4-Export-Library inkompatibel.');
      cleanup(null, null, false);
      return;
    }

    try {
      const muxer = new Muxer({
        target: new ArrayBufferTarget(),
        video: { codec: 'avc', width: REC_SIZE, height: REC_SIZE },
        // fastStart: 'in-memory' — Muxer buffert die ganze Datei und schreibt
        // beim Finalize das `moov`-Atom an den Anfang um. Resultat: klassisches
        // MP4 das in QuickTime, VLC, Windows Media Player, Mobil-Playern und
        // Social-Media-Uploads sofort abspielt.
        fastStart: 'in-memory',
      });

      let encoderError = null;
      const encoder = new VideoEncoder({
        output: (chunk, meta) => muxer.addVideoChunk(chunk, meta),
        error: e => { encoderError = e; console.error('[3D-MP4] VideoEncoder error:', e); },
      });

      encoder.configure({
        codec,
        width:        REC_SIZE,
        height:       REC_SIZE,
        // Desktop nutzt 80 Mbit/s, Mobile 16 Mbit/s. Beides wird auch im
        // Codec-Support-Check verwendet, damit Check und Encoder config
        // dieselbe Realität abfragen.
        bitrate:      BITRATE,
        framerate:    FPS,
        // Desktop darf auf Qualität optimieren; Mobile priorisiert kurze
        // Encoder-Queues und zuverlässiges Fertigwerden.
        latencyMode:  IS_MOBILE ? 'realtime' : 'quality',
        // VBR — Encoder darf in detail-reichen Frames (Label-Text während
        // Rotation) mehr Bits ausgeben als in einfacheren Frames.
        bitrateMode:  'variable',
      });

      // Frame für Frame: Bottle rotieren, rendern, composite, encode.
      // Keyframe-Intervall = 30 (jede Sek. ein I-Frame) für gutes Seeking.
      for (let i = 0; i < TOTAL_FRAMES; i++) {
        if (encoderError) throw encoderError;
        renderFrameI(i);
        const frame = new VideoFrame(composite, {
          timestamp: i * (1_000_000 / FPS),  // µs
          duration:  1_000_000 / FPS,
        });
        encoder.encode(frame, { keyFrame: i % FPS === 0 });
        frame.close();
        // Throttle wenn Encoder-Queue zu voll wird (verhindert OOM bei
        // sehr schnellem Frame-Push).
        const maxQueue = IS_MOBILE ? 2 : 4;
        while (encoder.encodeQueueSize > maxQueue) {
          await new Promise(r => setTimeout(r, IS_MOBILE ? 16 : 1));
        }
        const pct = Math.min(99, Math.round(((i + 1) / TOTAL_FRAMES) * 100));
        setProgress(pct, `${pct} %`);
        // Yield zum nächsten rAF für responsive UI während Encode.
        await new Promise(r => requestAnimationFrame(r));
      }

      // Flush + finalize sind auf Mobile mehrere Sekunden teuer — Progress-
      // Bar wäre sonst stumm bei 99 % stehen. Zwischenlabel macht klar dass
      // weiterhin gearbeitet wird.
      setProgress(99, 'Finalisiere …');
      await encoder.flush();
      if (encoderError) throw encoderError;
      encoder.close();
      muxer.finalize();
      setProgress(100, '100 %');

      const buffer = muxer.target.buffer;
      console.log('[3D-MP4] MP4 fertig:', buffer.byteLength, 'Bytes');
      const blob = new Blob([buffer], { type: 'video/mp4' });
      cleanup(blob, 'mp4');
    } catch (e) {
      console.error('[3D-MP4] Export fehlgeschlagen:', e);
      alert('MP4-Export ist fehlgeschlagen. Details in der Browser-Konsole.');
      cleanup(null, null, false);
    }
  };

  // ── Init-Promise: wartet aufs GLB ────────────────────────────────────────
  // Erst wenn das Modell geladen ist (labelMesh + deckelMesh assigned),
  // resolvet das Init-Promise. enter3DMode() in koebi-label-editor.js
  // ruft bottleApplyLabel() im .then() — der Frühreturn auf labelMesh=null
  // (alter Bug auf Mobile) kann damit nicht mehr passieren.
  await glbPromise;

  } // ── /initBottle3D() ─────────────────────────────────────────────────────

})();
